import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useUser } from '@auth0/nextjs-auth0/client';
import { cx } from 'class-variance-authority';

import {
  BackendQueryKeys,
  useTypeSafeMutation,
  useTypeSafeQuery,
  Voucher,
} from '@oresundsbron/api';
import { LoadingButton } from '@oresundsbron/bridge-ui';
import { CustomerType } from '@oresundsbron/validators';

import { useAuth, getUserCode, authenticate } from '~/stores/auth';

import * as gtag from '../../lib/gtag';
import { Aquire } from './Aquire';

export const AquireModal = ({
  voucher,
  title,
  className = '',
}: {
  voucher: Voucher;
  title: string;
  className?: string;
}) => {
  const router = useRouter();
  const [showVoucher, toggleVoucher] = useState(false);
  const [loading, toggleLoading] = useState(false);
  const [customerNo, setCustomerNo] = useState<string | undefined>();
  const { identifier } = useAuth();
  const { user, isLoading } = useUser();

  const { mutate } = useTypeSafeMutation(
    ['voucher', 'collect', { entryId: voucher.sys.id }],
    (req) => req.Voucher.collect
  );

  const { refetch, data } = useTypeSafeQuery(
    BackendQueryKeys.Voucher({ id: voucher.sys.id }),
    (req) =>
      req.Voucher.get({
        entryId: voucher.sys.id,
        customerNo: identifier,
        acCustomerNo: getUserCode() ?? '',
      }),
    {
      enabled: false,
      retry: false,
      retryOnMount: false,
      onSuccess: () => {
        mutate({
          entryId: voucher.sys.id,
          customerNo: identifier,
          acCustomerNo: getUserCode() ?? '',
        });
      },
      onSettled: (_, error) => {
        toggleLoading(false);
        if (error) {
          router.replace(
            `/api/auth/login?locale=${router.locale}&source=webclub&returnUrl=${router.asPath}`
          );
        } else {
          toggleVoucher(true);
          gtag.sendOpenClubVoucherModal({
            voucherName: title,
            discount: voucher.discount.replace(/\D+/g, ''), // Only keep digits
          });
        }
      },
    }
  );

  const { t } = useTranslation(['club', 'common'], { nsMode: 'fallback' });

  useEffect(
    () => () => {
      toggleLoading(false);
      toggleVoucher(false);
    },
    []
  );

  useEffect(() => {
    async function handleUser() {
      if (!user || isLoading) {
        return;
      }
      const accessTokenResponse = await fetch('/api/auth/access-token');
      const accessToken = await accessTokenResponse.json();
      if (accessToken) {
        // temporary set as private
        authenticate(accessToken, CustomerType.privateCustomer, '', false);
      }
    }
    handleUser();
  }, [isLoading, user]);

  return (
    <div className={className}>
      <LoadingButton
        loadingLabel={t('action.loading')}
        className={cx([
          [
            'text-white',
            'border-2',
            'border-white',
            'bg-primary-950',
            'hover:bg-white',
            'hover:!text-black',
            'hover:!border-primary-950',
            'focus:!border-black',
            'focus:bg-white',
            'focus:text-black',
            'focus:shadow-button-focus',
            'focus:ring-2',
            'focus:ring-white',
            'active:bg-white',
            'active:!text-black',
          ],
        ])}
        isLoading={loading}
        inverted={true}
        onClick={() => {
          toggleLoading(true);
          refetch();
        }}
      >
        {t('club.voucher.action')}
      </LoadingButton>
      <Aquire
        open={showVoucher}
        {...voucher}
        eanCode={data?.eanCode}
        urlToDiscount={data?.urlToDiscount}
        title={title}
        onChange={toggleVoucher}
        customerNo={customerNo}
      />
    </div>
  );
};
